/**
 * Contour levels and colour tables for 3D models (EarthImager 3D manual 5.1): N levels between a min and
 * max, linear or log10 spaced, one colour per level, banded (the manual's look) or continuous. Pure.
 */

const hex = (s) => [1, 3, 5].map((i) => parseInt(s.slice(i, i + 2), 16));
const stops = (list) => list.map(hex);

/** Preset colour spectra as stops, low value first. `ei-rainbow` is EarthImager's default blue-green-red. */
const PRESETS = {
  'ei-rainbow': stops(['#0000ff', '#00aaff', '#00ff66', '#aaff00', '#ffff00', '#ff8800', '#ff0000']),
  viridis: stops(['#440154', '#414487', '#2a788e', '#22a884', '#7ad151', '#fde725']),
  turbo: stops(['#30123b', '#4662d7', '#36aaf9', '#1ae4b6', '#72fe5e', '#c7ef34', '#faba39', '#f66b19', '#ca2a04', '#7a0403']),
  grey: stops(['#000000', '#ffffff']),
  'red-blue': stops(['#b2182b', '#ef8a62', '#f7f7f7', '#67a9cf', '#2166ac']),
  terrain: stops(['#2b6cb0', '#38a169', '#a3c46a', '#e8d9a0', '#b08968', '#7a5c4a', '#ffffff']),
};

export const CUSTOM_TABLE = 'custom';

export const COLOUR_TABLES = [
  { id: 'ei-rainbow', label: 'EarthImager rainbow' },
  { id: 'viridis', label: 'Viridis' },
  { id: 'turbo', label: 'Turbo' },
  { id: 'grey', label: 'Grey' },
  { id: 'red-blue', label: 'Red - blue' },
  { id: 'terrain', label: 'Terrain' },
];

export const MIN_LEVELS = 4;
export const MAX_LEVELS = 32;
export const DEFAULT_COLOUR = { count: 16, log: true, min: null, max: null, table: 'ei-rainbow', banded: true };

const clamp01 = (t) => (t < 0 ? 0 : t > 1 ? 1 : t);

function sample(stopList, t) {
  const p = clamp01(t) * (stopList.length - 1);
  const lo = Math.floor(p), hi = Math.min(stopList.length - 1, lo + 1), f = p - lo;
  return [0, 1, 2].map((k) => stopList[lo][k] + (stopList[hi][k] - stopList[lo][k]) * f);
}

/** `count` [r,g,b] (0..255) colours evenly along the named preset, low value first. */
export function colourTable(name, count) {
  const list = PRESETS[name] || PRESETS['ei-rainbow'];
  const n = Math.max(1, Math.round(count));
  return Array.from({ length: n }, (_, i) => sample(list, n === 1 ? 0.5 : i / (n - 1)));
}

const to2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
const HEX = /^#[0-9a-f]{6}$/i;

/** '#rrggbb' of an [r,g,b] colour (0..255). */
export function rgbToHex(c) {
  return `#${to2(c[0])}${to2(c[1])}${to2(c[2])}`;
}

/** `count` '#rrggbb' strings of a preset (the starting point of a custom table). */
export function presetHex(name, count) {
  return colourTable(name, count).map(rgbToHex);
}

/** `custom` ('#rrggbb' list) as [r,g,b] colours resampled to `count` entries; null when it holds no valid colour. */
export function customTable(custom, count) {
  const list = (Array.isArray(custom) ? custom : []).filter((h) => typeof h === 'string' && HEX.test(h)).map(hex);
  if (!list.length) return null;
  const n = Math.max(1, Math.round(count));
  if (list.length === n) return list;
  return Array.from({ length: n }, (_, i) => sample(list, n === 1 ? 0.5 : i / (n - 1)));
}

/** The custom list resized to `count` swatches (resampled along its colours). */
export function resizeCustom(custom, count) {
  const t = customTable(custom, count);
  return t ? t.map(rgbToHex) : null;
}

/**
 * Fill the swatches strictly between `from` and `to` (indices) by linear RGB interpolation of those two
 * anchors (the manual's anchor blending). Returns a new list; the anchors and the rest stay.
 */
export function blendCustom(custom, from, to) {
  const out = [...custom];
  const a = Math.max(0, Math.min(out.length - 1, Math.min(from, to)));
  const b = Math.max(0, Math.min(out.length - 1, Math.max(from, to)));
  if (b - a < 2) return out;
  const ca = hex(out[a]), cb = hex(out[b]);
  for (let i = a + 1; i < b; i++) {
    const f = (i - a) / (b - a);
    out[i] = rgbToHex([0, 1, 2].map((k) => ca[k] + (cb[k] - ca[k]) * f));
  }
  return out;
}

/** `count` level values from min to max inclusive, linear or log10 spaced (min > 0 for log). */
export function contourLevels({ min, max, count, log = false }) {
  const n = Math.max(2, Math.round(count));
  if (!(max > min)) return [min];
  const useLog = log && min > 0;
  const [a, b] = useLog ? [Math.log10(min), Math.log10(max)] : [min, max];
  return Array.from({ length: n }, (_, i) => {
    const v = a + ((b - a) * i) / (n - 1);
    return i === 0 ? min : i === n - 1 ? max : useLog ? 10 ** v : v;
  });
}

/** Position of `value` along the levels, 0..1 (log10 positions when `log`). */
export function levelPosition(value, levels, log = false) {
  const lo = levels[0], hi = levels[levels.length - 1];
  if (!(hi > lo)) return 0;
  if (log && lo > 0) return value > 0 ? clamp01((Math.log10(value) - Math.log10(lo)) / (Math.log10(hi) - Math.log10(lo))) : 0;
  return clamp01((value - lo) / (hi - lo));
}

/** [r,g,b] 0..255 of `value`: the colour of its nearest level when banded, else interpolated along the table. */
export function cellColour(value, levels, table, { banded = true, log = false } = {}) {
  const t = levelPosition(value, levels, log);
  if (banded || table.length < 2) return table[Math.round(t * (table.length - 1))];
  return sample(table, t);
}

/** Min/max of the positive values of `cells[key]`, or null. */
export function dataRange(cells, key = 'resistivity') {
  let lo = Infinity, hi = -Infinity;
  for (const c of cells || []) {
    const v = c[key];
    if (v > 0) { if (v < lo) lo = v; if (v > hi) hi = v; }
  }
  return hi >= lo ? [lo, hi] : null;
}

/** Min/max of the positive values over several models ({ cells } each): the shared range of "levels apply to all models"; null when none. */
export function combinedRange(models, key = 'resistivity') {
  let r = null;
  (models || []).forEach((m) => {
    const d = dataRange(m.cells, key);
    if (d) r = r ? [Math.min(r[0], d[0]), Math.max(r[1], d[1])] : d;
  });
  return r;
}

/**
 * Resolve a colour spec ({count, log, min, max, table, banded}; min/max null = the data range) into
 * { levels, table, banded, log, min, max, rgb(value) -> [r,g,b] 0..255, alpha(value) -> 0..1 }. `opacity` is one 0..1 value per level (missing = 1). Null when there is no range.
 */
export function makeColourScale(spec, range) {
  const s = { ...DEFAULT_COLOUR, ...spec };
  const lo = Number.isFinite(s.min) ? s.min : range?.[0];
  const hi = Number.isFinite(s.max) ? s.max : range?.[1];
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  const count = Math.max(MIN_LEVELS, Math.min(MAX_LEVELS, Math.round(s.count)));
  const log = s.log && lo > 0;
  const levels = contourLevels({ min: lo, max: Math.max(hi, lo), count, log });
  const table = (s.table === CUSTOM_TABLE && customTable(s.custom, levels.length)) || colourTable(s.table, levels.length);
  return {
    levels, table, banded: s.banded, log, min: lo, max: hi,
    rgb: (v) => cellColour(v, levels, table, { banded: s.banded, log }),
    alpha: (v) => opacityFor(v, levels, s.opacity, log),
  };
}

/** Indices of the preselected contour levels: every `every`-th (the manual's default is every third). */
export function defaultLevelIndices(count, every = 3) {
  const e = Math.max(1, Math.round(every));
  const out = [];
  for (let i = e - 1; i < count; i += e) out.push(i);
  return out;
}

/** Index of the level whose colour band holds `value` (the nearest level along the table; log10 positions when `log`). */
export function levelIndex(value, levels, log = false) {
  if (!levels.length) return 0;
  return Math.round(levelPosition(value, levels, log) * (levels.length - 1));
}

/** Opacity 0..1 of `value` from the per-level `opacity` array (levels without an entry are opaque). */
export function opacityFor(value, levels, opacity, log = false) {
  if (!opacity || !opacity.length) return 1;
  const a = opacity[levelIndex(value, levels, log)];
  return Number.isFinite(a) ? clamp01(a) : 1;
}

/** `opacity` resized to `n` levels: kept values stay, new levels are opaque. */
export function resizeOpacity(opacity, n) {
  return Array.from({ length: n }, (_, i) => (Number.isFinite(opacity?.[i]) ? opacity[i] : 1));
}

/** Opacity presets over `n` levels: 'all' opaque, 'low' hides the lower half, 'high' hides the upper half. */
export function opacityPreset(kind, n) {
  const half = Math.floor(n / 2);
  return Array.from({ length: n }, (_, i) => (kind === 'low' ? (i < half ? 0 : 1) : kind === 'high' ? (i >= n - half ? 0 : 1) : 1));
}

/**
 * RGBA pixels (Uint8Array, w*h*4) of a w x h grid of resistivities coloured with `scale` (makeColourScale): the colour
 * table and banding, the per-level opacity in the alpha channel. Row `r` of `values` is texture row `r`; values that are
 * not finite and positive stay transparent. One code path for the fences and the cutting-plane slice.
 */
export function colourTexturePixels(values, w, h, scale) {
  const data = new Uint8Array(w * h * 4);
  if (!scale) return data;
  for (let i = 0; i < w * h; i++) {
    const v = values[i];
    if (!(v > 0) || !Number.isFinite(v)) continue;
    const a = scale.alpha(v);
    if (!(a > 0)) continue;
    const c = scale.rgb(v);
    const o = i * 4;
    data[o] = Math.round(c[0]); data[o + 1] = Math.round(c[1]); data[o + 2] = Math.round(c[2]); data[o + 3] = Math.round(a * 255);
  }
  return data;
}

/** The Levels block applies whenever any model is drawn: fence diagrams (2D sections) or 3D volumes. */
export function hasLevelModels(fences, volumes) {
  return (fences?.length || 0) > 0 || (volumes?.length || 0) > 0;
}
